import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ErrorCard from '../ErrorCard/ErrorCard';
import TotalCategoryResponse from '../../../types/Analytics/TotalCategoryResponse';
import generateColours from '../generateColours';

ChartJS.register(ArcElement, Tooltip, Legend);

interface TopicsDoughnutChartProps {
  topicsData?: TotalCategoryResponse;
  isSmallScreen?: boolean;
  hideLegend?: boolean;
  selectedGroups?: string[];
}

function TopicsDoughnutChart({
  topicsData,
  isSmallScreen,
  hideLegend,
  selectedGroups
}: TopicsDoughnutChartProps): React.ReactElement | null {
  const [chartData, setChartData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (topicsData?.categories) {
      const allTopics = topicsData.categories.filter(
        (cat) => cat.category.toLowerCase() !== 'missed utterance'
      );
      const colors = generateColours(topicsData.categories.length);

      const filteredTopics =
        selectedGroups && selectedGroups.length > 0
          ? allTopics.filter((cat) => selectedGroups.includes(cat.category))
          : allTopics;

      const formattedChartData = {
        labels: filteredTopics.map((cat) => cat.category),
        datasets: [
          {
            label: 'Messages by topic',
            data: filteredTopics.map((cat) => cat.count),
            backgroundColor: filteredTopics.map((cat) => {
              const originalIndex = topicsData.categories.findIndex(
                (t) => t.category === cat.category
              );
              return colors[originalIndex];
            }),
            hoverOffset: 4
          }
        ]
      };
      setChartData(formattedChartData);
    } else {
      setChartData(null);
    }
  }, [topicsData, selectedGroups]);

  if (errorMessage) {
    return <ErrorCard errorMessage={errorMessage} />;
  }

  if (
    chartData &&
    chartData.datasets[0].data.reduce(
      (acc: number, val: number) => acc + val,
      0
    ) > 0
  ) {
    return (
      <div
        role='img'
        aria-label='Topics distribution chart'
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isSmallScreen ? 180 : 220
        }}
      >
        <Doughnut
          data={chartData}
          options={{
            cutout: '70%',
            maintainAspectRatio: false,
            responsive: true,
            animation: false,
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  boxWidth: 24,
                  padding: 10,
                  font: {
                    size: 11
                  }
                },
                display: !hideLegend
              },
              tooltip: {
                callbacks: {
                  label: (tooltipItem) => {
                    const dataset = tooltipItem.dataset.data;
                    const total = dataset.reduce((acc, val) => acc + val, 0);
                    const percentage = (
                      ((tooltipItem.raw as number) / total) *
                      100
                    ).toFixed(1);
                    return ` ${tooltipItem.raw} messages (${percentage}%)`;
                  }
                }
              }
            },
            layout: {
              padding: {
                left: 16,
                right: 16,
                top: 16,
                bottom: 8
              }
            }
          }}
        />
      </div>
    );
  }

  return null;
}

export default TopicsDoughnutChart;
